import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { 
  Database, Activity, Wifi, WifiOff, Clock, 
  Zap, Cpu, Battery, BarChart3, RefreshCw,
  AlertCircle, CheckCircle, TrendingUp, Download
} from 'lucide-react';

const Select = ({ value, onChange, options }) => (
  <div className="mb-4">
    <select 
      value={value} 
      onChange={(e) => onChange(e.target.value)}
      className="p-2 px-4 border border-gray-600 rounded-lg bg-gray-800 text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm font-medium w-full"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value} className="bg-gray-800">
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const BarChartAVkW = () => {
  const [viewType, setViewType] = useState('realtime');
  const [realTimeData, setRealTimeData] = useState([]);
  const [aggregatedData, setAggregatedData] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isReceivingData, setIsReceivingData] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [dataStats, setDataStats] = useState({
    receivedCount: 0,
    storageInterval: '1 minute',
    bufferSize: 0,
    aggregatedCount: 0
  });
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const dataCountRef = useRef(0);
  const aggregatedCountRef = useRef(0);
  const maxDataPoints = 60; // 1 minute of data at 1 per second

  // Initialize WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      const wsUrl = process.env.REACT_APP_ENDPOINT_WS || 'ws://localhost:5000';
      
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('BarChart WebSocket connected');
          setIsConnected(true);
          setConnectionStatus('connected');
          dataCountRef.current = 0;
          aggregatedCountRef.current = 0;
          setDataStats(prev => ({ 
            ...prev, 
            receivedCount: 0,
            aggregatedCount: 0
          }));
        };

        wsRef.current.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            const now = new Date();
            
            // Handle connection message
            if (message.type === 'connection') {
              console.log(`BarChart: Storage interval = ${message.interval}`);
              setDataStats(prev => ({ 
                ...prev, 
                storageInterval: message.interval || '1 minute' 
              }));
              return;
            }
            
            // Handle aggregated data
            if (message.type === 'aggregated' && message.dataType === 'power') {
              console.log('BarChart aggregated data received:', message.data);
              aggregatedCountRef.current++;
              
              const aggregatedEntry = {
                timestamp: new Date(message.data.timestamp || now),
                avgCurrent: message.data.avgCurrent || 0,
                avgVoltage: message.data.avgVoltage || 0,
                avgPower: message.data.avgPower || 0,
                minCurrent: message.data.minCurrent || 0,
                maxCurrent: message.data.maxCurrent || 0,
                minVoltage: message.data.minVoltage || 0,
                maxVoltage: message.data.maxVoltage || 0,
                minPower: message.data.minPower || 0,
                maxPower: message.data.maxPower || 0,
                sampleCount: message.data.sampleCount || 1
              };
              
              setAggregatedData(prev => {
                const updated = [...prev, aggregatedEntry];
                // Keep last 100 aggregated points (approx 100 minutes)
                return updated.slice(-100);
              });
              
              setDataStats(prev => ({ 
                ...prev, 
                aggregatedCount: aggregatedCountRef.current 
              }));
              return;
            }
            
            // Handle real-time data
            if (message.type === 'realtime') {
              handleIncomingData(message, now);
            } 
            // Handle legacy format
            else if (!message.type && (message.current !== undefined || message.voltage !== undefined)) {
              handleIncomingData(message, now);
            }
          } catch (error) {
            console.error('Error parsing WebSocket data:', error);
          }
        };

        wsRef.current.onclose = () => {
          console.log('BarChart WebSocket disconnected');
          setIsConnected(false);
          setIsReceivingData(false);
          setConnectionStatus('disconnected');
          
          // Attempt to reconnect after 3 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            if (wsRef.current?.readyState !== WebSocket.OPEN) {
              connectWebSocket();
            }
          }, 3000);
        };

        wsRef.current.onerror = (error) => {
          console.error('BarChart WebSocket error:', error);
          setIsConnected(false);
          setConnectionStatus('error');
        };
      } catch (error) {
        console.error('Error creating WebSocket:', error);
        setConnectionStatus('error');
      }
    };

    connectWebSocket();

    // Cleanup on component unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const handleIncomingData = (data, timestamp) => {
    const newDataPoint = {
      timestamp: timestamp,
      current: parseFloat(data.current) || 0,
      voltage: parseFloat(data.voltage) || 0,
      power: parseFloat(data.power) || 0,
      energy: parseFloat(data.energy) || 0,
      frequency: parseFloat(data.frequency) || 0,
    };

    setRealTimeData(prev => {
      const updated = [...prev, newDataPoint];
      // Keep only the last N data points
      if (updated.length > maxDataPoints) {
        return updated.slice(-maxDataPoints);
      }
      return updated;
    });

    setLastUpdate(timestamp);
    setIsReceivingData(true);
    dataCountRef.current++;
    
    // Update stats periodically
    if (dataCountRef.current % 10 === 0) {
      setDataStats(prev => ({ 
        ...prev, 
        receivedCount: dataCountRef.current,
        bufferSize: realTimeData.length
      }));
    }
  };

  // Generate chart data based on view type
  const getChartData = useMemo(() => {
    if (viewType === 'realtime') {
      // For real-time view, show the latest data points
      const categories = realTimeData.map((d, index) => {
        const time = new Date(d.timestamp);
        return `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}:${time.getSeconds().toString().padStart(2, '0')}`;
      });

      return {
        series: [
          { name: "Current (A)", data: realTimeData.map(d => d.current) },
          { name: "Voltage (V)", data: realTimeData.map(d => d.voltage) },
          { name: "Power (W)", data: realTimeData.map(d => d.power) },
        ],
        categories,
        type: 'realtime'
      };
    } else if (viewType === 'hourly') {
      // Use aggregated data for hourly view
if (aggregatedData.length === 0 && realTimeData.length > 0) {
  // Fallback to real-time data grouping if no aggregated data
  const hourlyData = {};
        realTimeData.forEach(item => {
          const hour = new Date(item.timestamp).getHours();
          if (!hourlyData[hour]) {
            hourlyData[hour] = { current: [], voltage: [], power: [] };
          }
          hourlyData[hour].current.push(item.current);
          hourlyData[hour].voltage.push(item.voltage);
          hourlyData[hour].power.push(item.power);
        });

        const categories = Object.keys(hourlyData).sort((a, b) => a - b);
        
        return {
          series: [
            { 
              name: "Avg Current (A)", 
              data: categories.map(hour => {
                const sum = hourlyData[hour].current.reduce((a, b) => a + b, 0);
                return sum / hourlyData[hour].current.length;
              })
            },
            { 
              name: "Avg Voltage (V)", 
              data: categories.map(hour => {
                const sum = hourlyData[hour].voltage.reduce((a, b) => a + b, 0);
                return sum / hourlyData[hour].voltage.length;
              })
            },
            { 
              name: "Avg Power (W)", 
              data: categories.map(hour => {
                const sum = hourlyData[hour].power.reduce((a, b) => a + b, 0);
                return sum / hourlyData[hour].power.length;
              })
            },
          ],
          categories: categories.map(hour => `${hour}:00`),
          type: 'hourly'
        };
      }

      // Use aggregated data
      const categories = aggregatedData.map((d, index) => {
        const time = new Date(d.timestamp);
        return `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`;
      });

      return {
        series: [
          { name: "Avg Current (A)", data: aggregatedData.map(d => d.avgCurrent) },
          { name: "Avg Voltage (V)", data: aggregatedData.map(d => d.avgVoltage) },
          { name: "Avg Power (W)", data: aggregatedData.map(d => d.avgPower) },
        ],
        categories,
        type: 'hourly'
      };
    } else if (viewType === 'daily') {
      // Use aggregated data for daily view
      if (aggregatedData.length === 0) {
        return {
          series: [
            { name: "Avg Current (A)", data: [] },
            { name: "Avg Voltage (V)", data: [] },
            { name: "Avg Power (W)", data: [] },
          ],
          categories: [],
          type: 'daily'
        };
      }

      // Group aggregated data by day
      const dailyData = {};
      aggregatedData.forEach(item => {
        const date = new Date(item.timestamp).toDateString();
        if (!dailyData[date]) {
          dailyData[date] = { current: [], voltage: [], power: [] };
        }
        dailyData[date].current.push(item.avgCurrent);
        dailyData[date].voltage.push(item.avgVoltage);
        dailyData[date].power.push(item.avgPower);
      });

      const categories = Object.keys(dailyData);
      
      return {
        series: [
          { 
            name: "Avg Current (A)", 
            data: categories.map(date => {
              const sum = dailyData[date].current.reduce((a, b) => a + b, 0);
              return sum / dailyData[date].current.length;
            })
          },
          { 
            name: "Avg Voltage (V)", 
            data: categories.map(date => {
              const sum = dailyData[date].voltage.reduce((a, b) => a + b, 0);
              return sum / dailyData[date].voltage.length;
            })
          },
          { 
            name: "Avg Power (W)", 
            data: categories.map(date => {
              const sum = dailyData[date].power.reduce((a, b) => a + b, 0);
              return sum / dailyData[date].power.length;
            })
          },
        ],
        categories: categories.map(date => new Date(date).toLocaleDateString()),
        type: 'daily'
      };
    }

    // Fallback empty data
    return {
      series: [
        { name: "Current (A)", data: [] },
        { name: "Voltage (V)", data: [] },
        { name: "Power (W)", data: [] },
      ],
      categories: [],
      type: 'empty'
    };
  }, [viewType, realTimeData, aggregatedData]);

  const getConnectionStatus = () => {
    if (isReceivingData) return { 
      text: 'Receiving Data', 
      color: 'text-emerald-400', 
      bg: 'bg-emerald-400/10',
      border: 'border-emerald-400/20',
      icon: <Activity className="w-4 h-4" />,
      dot: 'bg-emerald-400 animate-pulse'
    };
    if (isConnected) return { 
      text: 'Connected', 
      color: 'text-blue-400', 
      bg: 'bg-blue-400/10',
      border: 'border-blue-400/20',
      icon: <Wifi className="w-4 h-4" />,
      dot: 'bg-blue-400'
    };
    if (connectionStatus === 'error') return { 
      text: 'Connection Error', 
      color: 'text-rose-400', 
      bg: 'bg-rose-400/10',
      border: 'border-rose-400/20',
      icon: <AlertCircle className="w-4 h-4" />,
      dot: 'bg-rose-400'
    };
    return { 
      text: 'Disconnected', 
      color: 'text-gray-400', 
      bg: 'bg-gray-400/10',
      border: 'border-gray-400/20',
      icon: <WifiOff className="w-4 h-4" />,
      dot: 'bg-gray-400'
    };
  };

  const connectionStatusInfo = getConnectionStatus();

  const chartOptions = {
    chart: {
      type: "bar",
      height: 350,
      stacked: false,
      toolbar: { 
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      foreColor: "#ffffff",
      animations: {
        enabled: true,
        easing: 'linear',
        speed: 300,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: viewType === 'realtime' ? "70%" : "55%",
        endingShape: "rounded",
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: getChartData.categories,
      labels: {
        style: {
          fontSize: '11px',
          fontFamily: 'Inter, sans-serif',
          colors: "#94a3b8",
        },
        rotate: viewType === 'daily' ? -45 : 0,
        hideOverlappingLabels: true,
        maxHeight: 80,
      },
      title: {
        text: viewType === 'realtime' ? 'Time (HH:MM:SS)' : 
              viewType === 'hourly' ? 'Hour of Day' : 'Date',
        style: {
          color: '#cbd5e1',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
        }
      },
      axisBorder: {
        show: true,
        color: '#475569',
      },
      axisTicks: {
        show: true,
        color: '#475569',
      }
    },
    yaxis: [
      { 
        title: { 
          text: "Current (A)",
          style: {
            color: '#3b82f6',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
          }
        }, 
        labels: {
          style: {
            colors: '#3b82f6',
            fontSize: '11px',
            fontWeight: 500,
          },
          formatter: (val) => val.toFixed(1)
        },
        forceNiceScale: true,
        min: 0,
      },
      { 
        opposite: true, 
        title: { 
          text: "Voltage (V)",
          style: {
            color: '#10b981',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
          }
        }, 
        labels: {
          style: {
            colors: '#10b981',
            fontSize: '11px',
            fontWeight: 500,
          },
          formatter: (val) => val.toFixed(1)
        },
        forceNiceScale: true,
        min: 0,
      },
      { 
        opposite: true, 
        title: { 
          text: "Power (W)",
          style: {
            color: '#f59e0b',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
          }
        }, 
        labels: {
          style: {
            colors: '#f59e0b',
            fontSize: '11px',
            fontWeight: 500,
          },
          formatter: (val) => val.toFixed(0)
        },
        forceNiceScale: true,
        min: 0,
      },
    ],
    tooltip: {
      theme: "dark",
      x: {
        formatter: function(val, { series, seriesIndex, dataPointIndex, w }) {
          return getChartData.categories[dataPointIndex] || val;
        }
      },
      y: [
        { formatter: (val) => `${val.toFixed(2)} A` },
        { formatter: (val) => `${val.toFixed(2)} V` },
        { formatter: (val) => `${val.toFixed(2)} W` },
      ],
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '12px',
      },
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const currentVal = series[0][dataPointIndex];
        const voltageVal = series[1][dataPointIndex];
        const powerVal = series[2][dataPointIndex];
        
        return `
          <div class="p-3 bg-gray-800 border border-gray-700 rounded-lg shadow-xl">
            <div class="text-sm font-semibold text-gray-300 mb-2">
              ${getChartData.categories[dataPointIndex] || 'Unknown Time'}
            </div>
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span class="text-sm text-gray-300">Current</span>
                </div>
                <span class="text-sm font-bold text-white">${currentVal.toFixed(2)} A</span>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded-full bg-green-500"></div>
                  <span class="text-sm text-gray-300">Voltage</span>
                </div>
                <span class="text-sm font-bold text-white">${voltageVal.toFixed(2)} V</span>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span class="text-sm text-gray-300">Power</span>
                </div>
                <span class="text-sm font-bold text-white">${powerVal.toFixed(2)} W</span>
              </div>
            </div>
          </div>
        `;
      }
    },
    fill: { 
      opacity: 1,
      gradient: {
        shade: 'dark',
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: undefined,
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.2,
        stops: [0, 90, 100]
      }
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      fontWeight: 600,
      labels: {
        colors: '#cbd5e1',
        useSeriesColors: false,
      },
      markers: {
        width: 12,
        height: 12,
        radius: 6,
        strokeWidth: 0,
      },
      itemMargin: {
        horizontal: 15,
        vertical: 8
      }
    },
    colors: ['#3b82f6', '#10b981', '#f59e0b'],
    grid: { 
      borderColor: '#475569',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
  };

  const clearData = () => {
    setRealTimeData([]);
    setAggregatedData([]);
    dataCountRef.current = 0;
    aggregatedCountRef.current = 0;
    setDataStats(prev => ({ 
      ...prev, 
      receivedCount: 0,
      aggregatedCount: 0,
      bufferSize: 0
    }));
  };

  const exportData = () => {
    const dataToExport = viewType === 'realtime' ? realTimeData : aggregatedData;
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Timestamp,Current (A),Voltage (V),Power (W)\n"
      + dataToExport.map(d => 
          `${new Date(d.timestamp).toISOString()},${d.current || d.avgCurrent},${d.voltage || d.avgVoltage},${d.power || d.avgPower}`
        ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `power_data_${viewType}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl p-6 border border-gray-700">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="text-blue-400" size={24} />
            Electrical Parameters Analysis
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Real-time monitoring with data aggregation
          </p>
        </div>
        
        <div className="flex flex-wrap gap-4 items-center">

          
          {/* Data Stats */}
          <div className="flex items-center gap-3 px-4 py-2 bg-gray-800/50 rounded-xl border border-gray-600">
            <Database className="w-4 h-4 text-blue-400" />
            <div>
              <div className="text-sm font-semibold text-white">{dataStats.receivedCount}</div>
              <div className="text-xs text-gray-400">Data Points</div>
            </div>
          </div>
        </div>
      </div>

      {/* View Selection and Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <div className="flex-1">
          <Select
            value={viewType}
            onChange={setViewType}
            options={[
              { value: 'realtime', label: '📊 Real-time (Last 60 Seconds)' },
              { value: 'hourly', label: '⏰ Hourly Averages (Aggregated Data)' },
              { value: 'daily', label: '📅 Daily Averages (Aggregated Data)' }
            ]}
          />
        </div>
        
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={clearData}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors border border-gray-600"
          >
            <RefreshCw size={16} />
            Clear Data
          </button>
          <button 
            onClick={exportData}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            disabled={realTimeData.length === 0 && aggregatedData.length === 0}
          >
            <Download size={16} />
            Export Data
          </button>
        </div>
      </div>

         
      {/* Chart Area */}
      <div className="rounded-xl p-4 bg-gradient-to-br from-gray-800/50 to-gray-800/30 border border-gray-700 mb-6">
        {realTimeData.length === 0 && aggregatedData.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center">
            <div className="text-center">
              <div className="relative mb-6">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-700 border-t-blue-500 mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Zap className="text-blue-400" size={24} />
                </div>
              </div>
              <p className="text-gray-300 font-medium mb-2">Waiting for power data...</p>
              <p className="text-gray-500 text-sm">
                {isConnected ? 'Connected to WebSocket' : 'Connecting to data source...'}
              </p>
              <div className="mt-4 text-xs text-gray-600">
                Storage interval: {dataStats.storageInterval}
              </div>
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* Chart Overlay Info */}
            <div className="absolute top-4 right-4 z-10 flex items-center gap-3">
              {lastUpdate && (
                <div className="text-xs text-gray-300 bg-gray-900/80 px-3 py-1.5 rounded-full flex items-center gap-2">
                  <Clock size={12} />
                  Updated: {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              <div className="text-xs px-3 py-1.5 rounded-full bg-gray-800/80 text-gray-300">
                {viewType === 'realtime' ? 'Real-time' : 'Aggregated'} View
              </div>
            </div>
            
            <ReactApexChart
              options={chartOptions}
              series={getChartData.series}
              type="bar"
              height={350}
            />
          </div>
        )}
      </div>

      {/* Legend and Stats */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm bg-blue-500"></div>
            <div>
              <div className="text-sm font-medium text-white">Current</div>
              <div className="text-xs text-gray-400">{getChartData.series[0]?.data?.length || 0} points</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm bg-green-500"></div>
            <div>
              <div className="text-sm font-medium text-white">Voltage</div>
              <div className="text-xs text-gray-400">{getChartData.series[1]?.data?.length || 0} points</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm bg-yellow-500"></div>
            <div>
              <div className="text-sm font-medium text-white">Power</div>
              <div className="text-xs text-gray-400">{getChartData.series[2]?.data?.length || 0} points</div>
            </div>
          </div>
        </div>
        
        <div className="text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} />
            <span>
              {viewType === 'realtime' ? 'Real-time data' : 
               viewType === 'hourly' ? 'Hourly averages' : 
               'Daily averages'} • {dataStats.storageInterval} storage
            </span>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      {(realTimeData.length > 0 || aggregatedData.length > 0) && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity size={20} />
            Summary Statistics
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-gray-800/50 to-gray-800/30 rounded-xl border border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <div className="text-gray-400">Current</div>
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              </div>
              <div className="text-2xl font-bold text-blue-400">
                {getChartData.series[0]?.data?.length > 0 
                  ? Math.max(...getChartData.series[0].data).toFixed(2)
                  : '0.00'} A
              </div>
              <div className="text-sm text-gray-500 mt-1">Peak Current</div>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-gray-800/50 to-gray-800/30 rounded-xl border border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <div className="text-gray-400">Voltage</div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="text-2xl font-bold text-green-400">
                {getChartData.series[1]?.data?.length > 0 
                  ? Math.max(...getChartData.series[1].data).toFixed(2)
                  : '0.00'} V
              </div>
              <div className="text-sm text-gray-500 mt-1">Peak Voltage</div>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-gray-800/50 to-gray-800/30 rounded-xl border border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <div className="text-gray-400">Power</div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              </div>
              <div className="text-2xl font-bold text-yellow-400">
                {getChartData.series[2]?.data?.length > 0 
                  ? Math.max(...getChartData.series[2].data).toFixed(2)
                  : '0.00'} W
              </div>
              <div className="text-sm text-gray-500 mt-1">Peak Power</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarChartAVkW;